/**
 * MvrStateMarginPanel — MVR by state: our price against what the state charges us, and what this month earned.
 *
 * A "both" lookup in a state with separate Title and Registration prices bills (and costs) both records;
 * anywhere else it is one price and one fee, the same way StateDMVController bills it. A state fee nobody has
 * entered stays unknown rather than being guessed, and that state's margin is left uncounted.
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle, Download, Loader2, Map } from 'lucide-react';
import { getErrorMessage } from '../../../services/apiService';
import { MvrMarginReport, MvrPricingRow, MvrStateMargin, money, updateStateFee } from '../../../services/marginsService';

const FeeInput: React.FC<{ row: MvrPricingRow; onSaved: () => void }> = ({ row, onSaved }) => {
  const [value, setValue] = useState(row.stateFee == null ? '' : String(row.stateFee));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setValue(row.stateFee == null ? '' : String(row.stateFee)); }, [row.stateFee]);

  if (!row.pricingId) return <span className="text-[#94a3b8]" title="No price row for this state; it uses the default price">—</span>;

  const save = async () => {
    const trimmed = value.trim();
    const next = trimmed === '' ? null : parseFloat(trimmed);
    if (next != null && (!Number.isFinite(next) || next < 0)) { setError('Not a valid fee'); return; }
    if (next === row.stateFee) return;
    setSaving(true);
    setError('');
    try {
      await updateStateFee(row.pricingId!, next);
      onSaved();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <span className="inline-flex items-center gap-1">
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        placeholder="unknown"
        inputMode="decimal"
        disabled={saving}
        title={error || 'What the state charges us per lookup. Leave empty if unknown.'}
        className={`w-20 text-right px-2 py-1 rounded border text-sm ${error ? 'border-red-400' : 'border-black/[0.12]'} bg-white`}
      />
      {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#64748b]" />}
    </span>
  );
};

const csvCell = (v: string | number | null | undefined): string => {
  if (v == null) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const avgBilled = (s: MvrStateMargin): number | null => (s.lookups > 0 ? s.revenue / s.lookups : null);

/**
 * True when a single-price state's lookups this month billed at something other than today's price: an older price
 * (TX/AL/MO/MT before 2026-10-25) or a client's own price. Separate Title/Registration states mix record types in one
 * average, so they are not flagged rather than flagged wrongly.
 */
const billedDiffers = (s: MvrStateMargin): boolean => {
  const avg = avgBilled(s);
  return avg != null && !s.separateRecordPricing && s.pricingRows.length === 1 && Math.abs(avg - s.pricingRows[0].price) >= 0.005;
};

/** One row per state and record type, every state, for opening in Excel. Built from the report already loaded. */
const exportCsv = (report: MvrMarginReport) => {
  const header = ['State', 'State name', 'Record type',
    'Today: our price', 'Today: state fee', 'Today: margin per lookup',
    'Month: lookups', 'Month: avg billed per lookup', 'Month: billed total', 'Month: state cost', 'Month: margin', 'Flags'];
  const lines = [header.join(',')];
  for (const s of report.states) {
    const flags = [s.atOrBelowCost ? 'Priced at or below state fee' : '', s.feeUnknown ? 'Fee unknown' : '',
      billedDiffers(s) ? 'Billed at a different price this month' : ''].filter(Boolean).join('; ');
    const avg = avgBilled(s);
    s.pricingRows.forEach((r, i) => {
      // Month totals are per state, so they go on the state's first row only and are not double counted.
      lines.push([
        s.stateCode, s.stateName, r.recordType ?? 'Any', r.price.toFixed(2),
        r.stateFee == null ? '' : r.stateFee.toFixed(2),
        r.marginPerLookup == null ? '' : r.marginPerLookup.toFixed(2),
        i === 0 ? s.lookups : '', i === 0 && avg != null ? avg.toFixed(2) : '', i === 0 ? s.revenue.toFixed(2) : '',
        i === 0 && s.lookups > 0 && s.stateCost != null ? s.stateCost.toFixed(2) : '',
        i === 0 && s.lookups > 0 && s.margin != null ? s.margin.toFixed(2) : '',
        i === 0 ? flags : '',
      ].map(csvCell).join(','));
    });
  }
  const blob = new Blob([lines.join('\r\n') + '\r\n'], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `state-margins-${report.year}-${String(report.month).padStart(2, '0')}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
};

interface Props {
  report: MvrMarginReport;
  onChanged: () => void;
}

const MvrStateMarginPanel: React.FC<Props> = ({ report, onChanged }) => {
  const [showAll, setShowAll] = useState(false);
  const states = showAll ? report.states : report.states.filter((s) => s.lookups > 0);
  const knownMargin = report.revenue - report.revenueWithUnknownCost - report.knownStateCost;
  const atOrBelow = report.states.filter((s) => s.atOrBelowCost).length;
  const unknown = report.states.filter((s) => s.feeUnknown).length;

  return (
    <div className="glass-card overflow-hidden">
      <div className="px-6 py-4 border-b border-black/[0.06] flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-[#475569] flex items-center gap-2">
          <Map className="w-4 h-4 text-[#c41e3a]" /> MVR by state
        </h2>
        <div className="flex items-center gap-4">
          <label className="text-xs text-[#64748b] inline-flex items-center gap-2">
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
            Show states with no lookups this month
          </label>
          <button onClick={() => exportCsv(report)} title="Every state, one row per record type"
            className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-black/[0.12] text-[#475569] hover:text-[#c41e3a] hover:border-[#c41e3a]/40">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
      </div>

      <div className="px-6 py-5 space-y-4">
        <div className="text-sm text-[#334155]">
          This month: {report.lookups.toLocaleString()} lookups billed {money(report.revenue)}. Where the state fee is known,
          state fees came to {money(report.knownStateCost)},{' '}
          <span className={knownMargin >= 0 ? 'text-emerald-700 font-semibold' : 'text-red-700 font-semibold'}>margin {money(knownMargin)}</span>.
          {report.revenueWithUnknownCost > 0 && <> {money(report.revenueWithUnknownCost)} of revenue is from states whose fee is unknown.</>}
        </div>
        {(atOrBelow > 0 || unknown > 0) && (
          <div className="text-xs text-amber-800 inline-flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" />
            {atOrBelow > 0 && `${atOrBelow} state${atOrBelow === 1 ? '' : 's'} priced at or below the state fee. `}
            {unknown > 0 && `${unknown} state${unknown === 1 ? '' : 's'} with no state fee on file.`}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-[#94a3b8]">
              <tr>
                <th colSpan={2} />
                <th colSpan={3} className="text-center pt-1 pb-1 text-[#475569] border-b-2 border-[#c41e3a]/30">Pricing today (per lookup)</th>
                <th className="w-4" />
                <th colSpan={5} className="text-center pt-1 pb-1 text-[#475569] border-b-2 border-[#475569]/30">This month (what actually billed)</th>
              </tr>
              <tr className="border-b border-black/[0.06]">
                <th className="text-left py-2">State</th>
                <th className="text-left">Record</th>
                <th className="text-right">Our price</th>
                <th className="text-right">State fee</th>
                <th className="text-right">Margin</th>
                <th />
                <th className="text-right">Lookups</th>
                <th className="text-right">Avg billed</th>
                <th className="text-right">Billed total</th>
                <th className="text-right">State cost</th>
                <th className="text-right">Margin</th>
              </tr>
            </thead>
            <tbody>
              {states.length === 0 && (
                <tr><td colSpan={11} className="py-4 text-center text-[#64748b]">No MVR lookups this month.</td></tr>
              )}
              {states.map((s) =>
                s.pricingRows.map((r, i) => (
                  <tr key={`${s.stateCode}-${r.recordType ?? 'all'}-${i}`} className={`${i === s.pricingRows.length - 1 ? 'border-b border-black/[0.04]' : ''} ${s.atOrBelowCost ? 'bg-red-50/60' : ''}`}>
                    <td className="py-1.5">{i === 0 ? <span title={s.stateName}>{s.stateCode}</span> : ''}</td>
                    <td className="text-[#64748b]">{r.recordType ?? (s.pricingRows.length === 1 ? 'Any' : '')}</td>
                    <td className="text-right">{money(r.price)}</td>
                    <td className="text-right"><FeeInput row={r} onSaved={onChanged} /></td>
                    <td className={`text-right ${r.marginPerLookup != null && r.marginPerLookup <= 0 ? 'text-red-700 font-semibold' : ''}`}>{money(r.marginPerLookup)}</td>
                    {i === 0 ? (
                      <>
                        <td rowSpan={s.pricingRows.length} />
                        <td className="text-right" rowSpan={s.pricingRows.length}>{s.lookups.toLocaleString()}</td>
                        <td className="text-right whitespace-nowrap" rowSpan={s.pricingRows.length}>
                          {billedDiffers(s) && (
                            <span title="Billed at a price different from today's: an older price, or a client's own price" className="text-amber-600 mr-1 cursor-help">⚠</span>
                          )}
                          {money(avgBilled(s))}
                        </td>
                        <td className="text-right" rowSpan={s.pricingRows.length}>{money(s.revenue)}</td>
                        <td className="text-right" rowSpan={s.pricingRows.length}>{s.lookups === 0 ? '—' : money(s.stateCost)}</td>
                        <td className={`text-right ${s.margin != null && s.margin < 0 ? 'text-red-700 font-semibold' : ''}`} rowSpan={s.pricingRows.length}>
                          {s.lookups === 0 ? '—' : s.margin == null ? <span className="text-[#94a3b8]">fee unknown</span> : money(s.margin)}
                        </td>
                      </>
                    ) : null}
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-[#64748b]">
          <span className="font-semibold">This month</span> is actual bills: margin = billed total − state fees for those lookups.
          A ⚠ means the month billed at a different price than today's (an older price, or a client's own price).{' '}
          State fees are our cost per lookup, not what clients pay; editing one changes nothing on any bill. Not counted here:
          annual subscriptions (SC, MD, NM and others), charges for no-hit lookups, and Mississippi's per-title history fee.
        </p>
      </div>
    </div>
  );
};

export default MvrStateMarginPanel;
